import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, Brain, Check, Gauge, Save, Sliders, TimerReset } from "lucide-react";

import type { Libro } from "../api/tipos.ts";
import { esDeCerteza } from "../api/tipos.ts";
import { ErrorApi } from "../api/cliente.ts";
import { usarConfiguracion, usarGuardarConfiguracion, usarLibro, usarMetricas } from "../ganchos/datos.ts";
import { usarSesion } from "../sesion/ContextoSesion.tsx";
import { PERMISO } from "../sesion/permisos.ts";
import { duracion, pesos, plural, porcentaje } from "../formato.ts";
import { calcularCuadre } from "./cuadre.ts";
import { CLAVES } from "./configuracion-modelo.ts";
import { ErrorConsulta } from "../componentes/Errores.tsx";
import { Rosca, type Porcion } from "../componentes/Rosca.tsx";
import { Aviso, Boton, Campo, Esqueleto, Insignia, Seccion, Tarjeta, cx } from "../componentes/ui.tsx";
import type { Tono } from "../vocabulario.ts";

const VENTANAS = [7, 30, 90] as const;

/**
 * Cómo va el sistema, no el día. Aquí vive lo que salió del Resumen en SPEC-029
 * §16 —el cuadre del mes y la rosca de cuánto se resolvió solo— y el umbral de
 * certeza, que salió de Configuración: es ayuda técnica, no un ajuste del día a
 * día.
 *
 * Dos medidas del sistema, no del recaudo.
 *
 * **Autonomía**: cuánto se resuelve solo. Es lo que justifica encender reglas.
 * **Reacción**: cuánto tarda una persona en decidir *después* de que se le
 * avisó. Si eso son horas, el aviso no está llegando a nadie y da igual lo bien
 * construido que esté el canal (SPEC-010 §9).
 */
export function Metricas() {
  const [dias, setDias] = useState<number>(30);
  const { data, isPending, error, refetch } = usarMetricas(dias);
  const { data: libro } = usarLibro();
  const { puedo } = usarSesion();
  const r = data?.reaccion;

  return (
    <Seccion
      titulo="Métricas de operación"
      descripcion="Cómo se está comportando el sistema — y si los avisos le llegan a alguien."
      accion={
        <div className="flex gap-1 rounded-xl border border-borde bg-superficie p-1">
          {VENTANAS.map((d) => (
            <button
              key={d}
              onClick={() => setDias(d)}
              className={cx(
                "rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
                dias === d ? "bg-marca text-white" : "text-tenue hover:text-tinta",
              )}
            >
              {d} días
            </button>
          ))}
        </div>
      }
    >
      {libro && <BarraDeCuadre libro={libro} />}

      {error ? (
        <ErrorConsulta error={error} alReintentar={() => void refetch()} />
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* — autonomía — */}
          <Tarjeta className="space-y-5 p-6">
            <Cabecera
              icono={Gauge}
              tono="marca"
              titulo="Autonomía"
              subtitulo="Fracción resuelta sin que nadie intervenga."
            />
            {libro ? (
              <>
                <Rosca
                  centro={porcentaje(libro.resumen.automatizacion, 0)}
                  pie="automático"
                  porciones={composicion(libro)}
                />
                {libro.resumen.porAprendizaje > 0 && (
                  <p className="flex items-start gap-2 rounded-xl bg-marca-suave p-3 text-sm text-marca-tinta">
                    <Brain className="mt-0.5 size-4 shrink-0" aria-hidden />
                    <span>
                      {libro.resumen.porAprendizaje}{" "}
                      {plural(libro.resumen.porAprendizaje, "pago se resolvió", "pagos se resolvieron")} con lo
                      que el equipo ya había confirmado antes.
                    </span>
                  </p>
                )}
                <dl className="grid grid-cols-2 gap-4 text-sm">
                  <Par termino="Resueltos" valor={`${libro.resumen.resueltos} de ${libro.resumen.total}`} />
                  <Par termino="Por identidad aprendida" valor={String(libro.resumen.porAprendizaje)} />
                  <Par termino="Por regla de autonomía" valor={String(libro.resumen.porRegla)} />
                  <Par termino="Confirmados a mano" valor={String(libro.resumen.porHumano)} />
                </dl>
                <p className="rounded-xl bg-superficie-2 p-3 text-xs leading-relaxed text-tenue">
                  Subir esta cifra es una decisión de producto, no una preferencia: encender una
                  regla exige medir con ella apagada, medir con ella encendida, y comprobar que los
                  falsos positivos siguen en cero.
                </p>
              </>
            ) : (
              <Esqueleto className="h-40 w-full" />
            )}
          </Tarjeta>

          {/* — reacción — */}
          <Tarjeta className="space-y-5 p-6">
            <Cabecera
              icono={BellRing}
              tono="dato"
              titulo="Reacción a los avisos"
              subtitulo={`Cuánto tarda alguien en decidir después de que se le avisó · últimos ${dias} días.`}
            />
            {isPending ? (
              <Esqueleto className="h-40 w-full" />
            ) : r ? (
              <>
                <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
                  <div>
                    <p className="text-xs text-tenue">Mediana</p>
                    <p className="cifra-titular text-3xl">
                      {duracion(r.medianaMinutos)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-tenue">El 90 % decide antes de</p>
                    <p className="cifra text-xl font-semibold tracking-tight text-tenue">
                      {duracion(r.p90Minutos)}
                    </p>
                  </div>
                </div>

                <Insignia tono={tonoLectura(r.medianaMinutos, r.decididos)} icono={TimerReset}>
                  {r.lectura}
                </Insignia>

                <dl className="grid grid-cols-3 gap-4 text-sm">
                  <Par termino="Avisados" valor={String(r.avisados)} />
                  <Par termino="Decididos" valor={String(r.decididos)} />
                  <Par termino="Sin decidir" valor={String(r.sinDecidir)} />
                </dl>

                {r.sinDecidir > 0 && (
                  <p className="rounded-xl bg-aviso-suave p-3 text-xs leading-relaxed text-aviso-tinta">
                    {r.sinDecidir} {plural(r.sinDecidir, "aviso salió", "avisos salieron")} y nadie
                    ha decidido todavía. Si el número no baja, el canal no está llegando a nadie.
                  </p>
                )}

                <p className="text-xs leading-relaxed text-tenue-2">
                  Se usa la mediana y no el promedio: una decisión tomada una semana después
                  arruinaría la media y escondería que el resto se atiende en minutos.
                </p>
              </>
            ) : null}
          </Tarjeta>
        </div>
      )}

      {puedo(PERMISO.MOVER_AUTONOMIA) && <UmbralDeCerteza />}
    </Seccion>
  );
}

/**
 * El umbral de certeza (SPEC-029 §16): salió de Configuración porque quien
 * abre Configuración va a cambiar el correo o los avisos, y encontrarse ahí un
 * número que decide a quién se le atribuye la plata es una invitación a
 * tocarlo. Aquí está junto a la medida que lo justifica.
 */
function UmbralDeCerteza() {
  const { data } = usarConfiguracion();
  const guardar = usarGuardarConfiguracion();
  const guardado = data?.claves.find((c) => c.clave === CLAVES.umbral);
  const actual = guardado?.definida ? guardado.valor : "";
  const [valor, setValor] = useState(actual);
  const [avisos, setAvisos] = useState<string[] | null>(null);
  useEffect(() => setValor(actual), [actual]);
  if (!data?.puedeAutonomia) return null;

  const error = guardar.error instanceof ErrorApi ? guardar.error.message : guardar.error?.message;
  const cambiado = valor.trim() !== actual.trim();

  return (
    <Tarjeta className="space-y-4 p-6">
      <Cabecera
        icono={Sliders}
        tono="marca"
        titulo="Umbral de certeza"
        subtitulo="Cuánta certeza exige 4drando para dar un pago por identificado sin preguntar. Solo el dueño."
      />
      <Aviso tono="aviso" titulo="Léelo antes de bajarlo">
        Bajarlo hace que empiece a dar por buenos pagos que solo coinciden en el monto, que es como se
        acaba atribuyendo la plata de un cliente a otro.
      </Aviso>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setAvisos(null);
          guardar.mutate({ [CLAVES.umbral]: valor.trim() || null }, { onSuccess: (r) => setAvisos(r.avisos) });
        }}
      >
        <div className="w-48">
          <Campo
            etiqueta="Umbral"
            type="number"
            placeholder="70"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
          />
        </div>
        <Boton type="submit" variante="suave" icono={Save} disabled={!cambiado} cargando={guardar.isPending}>
          Guardar
        </Boton>
      </form>
      {error ? (
        <p className="max-w-prose text-sm text-riesgo-tinta">{error}</p>
      ) : (
        <p className="text-xs text-tenue-2">Por debajo de 45 hace falta autorizarlo, y queda firmado con tu nombre.</p>
      )}
      {avisos !== null && (
        <Aviso tono={avisos.length ? "aviso" : "exito"} titulo={avisos.length ? "Guardado, y esto es lo que cambia" : "Guardado"}>
          {avisos.length ? (
            <ul className="ml-4 list-disc space-y-1">
              {avisos.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          ) : (
            "El umbral quedó al día."
          )}
        </Aviso>
      )}
    </Tarjeta>
  );
}

const tonoLectura = (mediana: number | null, decididos: number): Tono => {
  if (!decididos) return "peligro";
  if (mediana === null) return "neutro";
  if (mediana <= 15) return "exito";
  if (mediana <= 120) return "aviso";
  return "peligro";
};

const Cabecera = ({
  icono: Icono,
  tono,
  titulo,
  subtitulo,
}: {
  icono: typeof Gauge;
  tono: "marca" | "dato";
  titulo: string;
  subtitulo: string;
}) => (
  <div className="flex items-start gap-3">
    <span
      className={cx(
        "grid size-9 shrink-0 place-items-center rounded-xl",
        tono === "marca" ? "bg-marca-suave text-marca-tinta" : "bg-dato-suave text-dato-tinta",
      )}
    >
      <Icono className="size-4.5" aria-hidden />
    </span>
    <div>
      <h2 className="font-semibold tracking-tight">{titulo}</h2>
      <p className="mt-0.5 text-sm text-tenue">{subtitulo}</p>
    </div>
  </div>
);

const Par = ({ termino, valor }: { termino: string; valor: string }) => (
  <div>
    <dt className="text-xs text-tenue">{termino}</dt>
    <dd className="cifra mt-0.5 font-medium">{valor}</dd>
  </div>
);

/**
 * La composición se cuenta sobre los movimientos, no restando campos del
 * resumen: un pago que alguien *descartó* no es automático ni está pendiente, y
 * la resta lo colaría en el bloque equivocado sin que nadie lo notara.
 */
function composicion(libro: Libro): Porcion[] {
  const m = libro.movimientos;
  const cuenta = (pred: (x: (typeof m)[number]) => boolean) => m.filter(pred).length;

  return [
    {
      clave: "auto",
      valor: cuenta((x) => x.origen === "automatico"),
      texto: "4drando solo",
      tono: "dato",
    },
    {
      clave: "aprendido",
      valor: cuenta((x) => x.origen === "identidad-aprendida"),
      texto: "Reconocido por pagos anteriores",
      tono: "marca",
    },
    {
      clave: "regla",
      valor: cuenta((x) => x.origen === "regla-graduada"),
      texto: "Por regla automática",
      tono: "exito",
    },
    {
      clave: "confirmado",
      valor: cuenta((x) => x.origen === "humano" && esDeCerteza(x.estado)),
      texto: "Lo decidió alguien",
      tono: "humano",
    },
    {
      clave: "descartado",
      valor: cuenta((x) => x.origen === "humano" && !esDeCerteza(x.estado)),
      texto: "Descartado por alguien",
      tono: "neutro",
    },
    {
      clave: "pendiente",
      valor: libro.pendientes.length,
      texto: "Todavía sin decidir",
      tono: "aviso",
    },
  ].filter((p) => p.valor > 0) as Porcion[];
}

/**
 * El cuadre del mes como el solape de dos conjuntos.
 *
 * Antes esto eran cuatro tarjetas de estadística —pendientes, resueltos,
 * alertas, por cobrar— que obligaban a sumar de cabeza para responder la única
 * pregunta que importa: cuánto de lo que entró y de lo que esperabas es la
 * misma plata. La barra la responde de un vistazo, y el color dice de quién es
 * cada trozo: azul el banco, verde tú, teal donde se tocaron.
 */
function BarraDeCuadre({ libro }: { libro: Libro }) {
  const c = calcularCuadre(libro);
  const r = libro.resumen;
  const vacio = c.entroCentavos === 0 && c.esperadoCentavos === 0;

  return (
    <Tarjeta className="space-y-4 p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 text-[11px] font-semibold tracking-wider text-dato-tinta uppercase">
            <span className="size-2 rounded-[3px] bg-dato" aria-hidden />
            Entró al banco
          </p>
          <p className="cifra-titular mt-1 text-2xl sm:text-3xl">
            {pesos(c.entroCentavos)}
          </p>
        </div>
        <div className="text-right">
          <p className="flex items-center justify-end gap-2 text-[11px] font-semibold tracking-wider text-exito-tinta uppercase">
            Esperabas cobrar
            <span className="size-2 rounded-[3px] bg-exito" aria-hidden />
          </p>
          <p className="cifra-titular mt-1 text-2xl sm:text-3xl">
            {pesos(c.esperadoCentavos)}
          </p>
        </div>
      </div>

      {vacio ? (
        <div className="grid h-16 place-items-center rounded-xl border border-dashed border-borde text-sm text-tenue-2">
          Todavía no ha entrado ni un peso por aquí.
        </div>
      ) : (
        <div className="flex h-16 overflow-hidden rounded-xl bg-superficie-2">
          <div className="bg-dato/15" style={{ width: `${c.anchos.sinDueno}%` }} aria-hidden />
          <div
            className="flex min-w-0 items-center justify-center gap-2 bg-marca px-3 text-white"
            style={{ width: `${c.anchos.cuadrado}%` }}
          >
            {c.anchos.cuadrado > 22 && (
              <>
                <Check className="size-4 shrink-0" aria-hidden />
                <span className="cifra-titular truncate text-sm sm:text-base">
                  {pesos(c.cuadradoCentavos)} cuadrados
                </span>
              </>
            )}
          </div>
          <div className="bg-exito/15" style={{ width: `${c.anchos.sinLlegar}%` }} aria-hidden />
        </div>
      )}

      {/* Lo que queda a los lados es exactamente lo que hay que hacer. */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-sm">
        <Link to="/bandeja" className="text-tenue hover:text-tinta">
          <span className="cifra font-semibold text-tinta">{pesos(c.sinDuenoCentavos)}</span> entró y
          nadie sabe de quién es
          {r.pendientes > 0 && (
            <span className="ml-2 text-tenue-2">
              · {r.pendientes} {plural(r.pendientes, "espera", "esperan")} tu decisión
            </span>
          )}
        </Link>
        <Link to="/alertas" className="text-tenue hover:text-tinta">
          <span className="cifra font-semibold text-tinta">{pesos(c.sinLlegarCentavos)}</span>{" "}
          facturado que aún no llega
        </Link>
      </div>
    </Tarjeta>
  );
}

